import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { companyDocsApi } from '../services/companyDocsApi';
import { ErrorBanner, errorText } from '../components/ErrorBanner';
import type { CompanyDocRow as CompanyDoc, CompanyDocInput, DocRule, DocState, DocTypeInfo } from '../services/companyDocsApi';

/**
 * 업무 › 회사서류 (#159 — admin#101 에서 옮겨 옴) — 입찰에 내는 회사서류 원본 · 발급일 · 만료일.
 * 만료 30일 전부터 「곧 만료」, 지나면 「만료」. 프로젝트 › 제출서류 묶음이 같은 목록을 쓴다.
 */
const STATE: Record<DocState, [string, string]> = {
  expired: ['만료', 'bg-red-100 text-red-700'],
  expiring: ['곧 만료', 'bg-amber-50 text-amber-600'],
  valid: ['유효', 'bg-green-100 text-green-700'],
  'per-bid': ['입찰 때 발급', 'bg-slate-100 text-slate-600'],
};

const RULE_HINT: Record<DocRule, string> = {
  EXPIRES: '만료일을 넣어 주세요(파일명의 날짜가 만료일인 경우가 많습니다).',
  ISSUED_3M: '발급일을 넣어 주세요. 만료일을 비우면 발급일 + 3개월로 봅니다.',
  NONE: '만료가 없는 서류입니다. 바뀌면 새로 올려 주세요.',
  PER_BID: '입찰 때마다 새로 발급합니다. 발급일만 넣어 주세요.',
};

type Filter = 'all' | 'attention';

export const CompanyDocsPage: React.FC = () => {
  const { showToast, showConfirm } = useAlert();
  const [docs, setDocs] = useState<CompanyDoc[]>([]);
  const [types, setTypes] = useState<DocTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<CompanyDoc | 'new' | null>(null);
  /* 조회 실패 — 토스트는 사라져서 표가 「없음」으로 남았다. 배너로 두고 「다시 시도」를 붙인다(2026-10-27) */
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [d, t] = await Promise.all([companyDocsApi.list(), companyDocsApi.types()]);
      setDocs(d);
      setTypes(t);
    } catch (e) {
      setLoadError(`회사서류를 불러오지 못했습니다 — ${errorText(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const label = useMemo(() => Object.fromEntries(types.map((t) => [t.type, t.label])), [types]);
  const count = (s: DocState) => docs.filter((d) => d.state === s).length;
  const shown = filter === 'all' ? docs : docs.filter((d) => d.state === 'expired' || d.state === 'expiring');

  const download = async (doc: CompanyDoc) => {
    try {
      await companyDocsApi.download(doc);
    } catch (e) {
      showToast(`받지 못했습니다: ${(e as Error).message}`, 'error');
    }
  };

  const remove = async (doc: CompanyDoc) => {
    if (!(await showConfirm(`「${doc.name}」(${doc.fileName})을 목록에서 지울까요?`))) return;
    try {
      await companyDocsApi.remove(doc.id);
      showToast('지웠습니다', 'success');
      load();
    } catch (e) {
      showToast(`지우지 못했습니다: ${(e as Error).message}`, 'error');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">회사서류</h1>
          <p className="text-sm text-slate-500 mt-1">
            입찰에 내는 회사서류 원본과 유효기간. 만료 30일 전부터 「곧 만료」로 표시합니다.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          disabled={types.length === 0}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
        >
          서류 올리기
        </button>
      </header>

      <ErrorBanner message={loadError} onRetry={load} className="mb-4" />

      <div className="flex items-center gap-2 mb-4">
        <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
          전체 {docs.length}
        </FilterButton>
        <FilterButton active={filter === 'attention'} onClick={() => setFilter('attention')}>
          확인 필요 {count('expired') + count('expiring')}
        </FilterButton>
        {count('expired') > 0 && <span className="text-sm text-red-600">만료 {count('expired')}건</span>}
        {count('expiring') > 0 && <span className="text-sm text-amber-600">곧 만료 {count('expiring')}건</span>}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              {['서류', '이름', '발급일', '만료일', '상태', '원본', '메모', ''].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && docs.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                  불러오는 중…
                </td>
              </tr>
            ) : shown.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                  {loadError ? '불러오지 못했습니다.' : filter === 'all' ? '올린 서류가 없습니다.' : '확인할 서류가 없습니다.'}
                </td>
              </tr>
            ) : (
              shown.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{label[d.type] ?? d.type}</td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{d.name}</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums whitespace-nowrap">{d.issuedAt ?? '-'}</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums whitespace-nowrap">
                    {d.expiresAt ?? '-'}
                    {d.expiresAt && !d.expiresAtInput && (
                      <span className="ml-1 text-xs text-slate-400" title="발급일 + 3개월">
                        (계산)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${STATE[d.state][1]}`}>
                      {STATE[d.state][0]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => download(d)}
                      className="text-indigo-600 hover:underline text-left"
                      title={`${formatSize(d.fileSize)} 받기`}
                    >
                      {d.fileName}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{d.note ?? ''}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={() => setEditing(d)}
                      className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
                    >
                      고치기
                    </button>
                    <button type="button" onClick={() => remove(d)} className="px-2 py-1 text-xs text-red-600 hover:text-red-700">
                      지우기
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <DocForm
          doc={editing === 'new' ? null : editing}
          types={types}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
};

const FilterButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
      active ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

const INPUT = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none';

/** 올리기 · 고치기. 원본은 올릴 때만 — 바꾸려면 새로 올린다 */
const DocForm: React.FC<{
  doc: CompanyDoc | null;
  types: DocTypeInfo[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ doc, types, onClose, onSaved }) => {
  const { showToast } = useAlert();
  const [form, setForm] = useState<Required<CompanyDocInput>>({
    type: doc?.type ?? types[0]?.type ?? '',
    name: doc?.name ?? '',
    issuedAt: doc?.issuedAt ?? '',
    expiresAt: doc?.expiresAtInput ?? '',
    note: doc?.note ?? '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const rule = types.find((t) => t.type === form.type)?.rule ?? 'NONE';
  const set = (k: keyof CompanyDocInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doc && !file) return showToast('원본 파일을 골라 주세요', 'warning');
    setSaving(true);
    try {
      // 입찰 때 발급 서류는 만료일을 두지 않는다(종류를 바꾼 경우 남은 값 지움)
      const input = rule === 'PER_BID' ? { ...form, expiresAt: '' } : form;
      if (doc) await companyDocsApi.update(doc.id, input);
      else await companyDocsApi.create(input, file!);
      showToast(doc ? '고쳤습니다' : '올렸습니다', 'success');
      onSaved();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-lg shadow-2xl relative z-50">
        <header className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{doc ? '서류 고치기' : '서류 올리기'}</h2>
        </header>
        <div className="p-6 space-y-4">
          <Field label="종류">
            <select value={form.type} onChange={set('type')} className={INPUT}>
              {types.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">{RULE_HINT[rule]}</p>
          </Field>
          <Field label="이름 (비우면 종류 이름)">
            <input value={form.name} onChange={set('name')} className={INPUT} maxLength={200} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={`발급일${rule === 'ISSUED_3M' || rule === 'PER_BID' ? ' *' : ''}`}>
              <input
                type="date"
                value={form.issuedAt}
                onChange={set('issuedAt')}
                className={INPUT}
                required={rule === 'ISSUED_3M' || rule === 'PER_BID'}
              />
            </Field>
            <Field label={`만료일${rule === 'EXPIRES' ? ' *' : ''}`}>
              <input
                type="date"
                value={form.expiresAt}
                onChange={set('expiresAt')}
                className={INPUT}
                required={rule === 'EXPIRES'}
                disabled={rule === 'PER_BID'}
              />
            </Field>
          </div>
          <Field label="메모">
            <input value={form.note} onChange={set('note')} className={INPUT} />
          </Field>
          {doc ? (
            <p className="text-xs text-slate-500">원본({doc.fileName})을 바꾸려면 새로 올리고 이 줄을 지워 주세요.</p>
          ) : (
            <Field label="원본 (PDF · JPG · PNG, 10MB 까지) *">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-slate-600"
              />
            </Field>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"
          >
            닫기
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? '저장 중…' : doc ? '고치기' : '올리기'}
          </button>
        </footer>
      </form>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
    {children}
  </label>
);

function formatSize(n: number): string {
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`;
}

